
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { type TeamMember } from "@/types";
import { TeamMemberList } from "./team/TeamMemberList";
import { InviteMemberForm } from "./team/InviteMemberForm";
import { AdditionalMembersBilling } from "./team/AdditionalMembersBilling";
import { type InviteFormValues } from "./team/types";
import { getTeamMemberLimit, canAddTeamMember, calculateAdditionalMembersNeeded } from "@/utils/teamMemberLimits";
import { canInviteTeamMember } from "@/utils/planLimits";
import { UpgradePrompt } from "@/components/shared/UpgradePrompt";

export const TeamManagement = ({ 
  companyId,
  isPro
}: { 
  companyId: string;
  isPro: boolean;
}) => {
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [company, setCompany] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [showUpgradePrompt, setShowUpgradePrompt] = useState(false);
  const [upgradeMessage, setUpgradeMessage] = useState("");

  const fetchCompanyData = async () => {
    try {
      const { data: companyData, error: companyError } = await supabase
        .from("companies")
        .select("*")
        .eq("id", companyId)
        .single();

      if (companyError) {
        console.error("Error fetching company:", companyError);
        return;
      }

      setCompany(companyData);
    } catch (error) {
      console.error("Error fetching company data:", error);
    }
  };

  const fetchTeamMembers = async () => {
    try {
      const { data: members, error } = await supabase
        .from("team_members")
        .select(`
          id,
          email,
          name,
          status,
          user_id,
          created_at,
          user:profiles(full_name, avatar_url)
        `)
        .eq("company_id", companyId)
        .order("created_at", { ascending: false });

      if (error) {
        console.error("Error fetching team members:", error);
        toast.error("Failed to load team members");
        return;
      }

      if (members) {
        const formattedTeamMembers: TeamMember[] = members.map(member => ({
          id: member.id,
          email: member.email,
          name: member.name,
          status: member.status,
          user_id: member.user_id,
          created_at: member.created_at,
          profile: member.user ? {
            full_name: member.user[0]?.full_name,
            avatar_url: member.user[0]?.avatar_url
          } : null
        }));
        setTeamMembers(formattedTeamMembers);
      }
    } catch (error) {
      console.error("Error formatting team members:", error);
      toast.error("Failed to process team members data");
    } finally {
      setIsInitialLoading(false);
    }
  };

  useEffect(() => {
    fetchCompanyData();
    fetchTeamMembers();
  }, [companyId]);

  const activeMemberCount = teamMembers.filter(member => member.status === 'active').length;
  const baseLimit = company ? getTeamMemberLimit(company.plan) : 0;
  const additionalMembersNeeded = calculateAdditionalMembersNeeded(activeMemberCount + 1, company?.plan || 'free');

  const onSubmit = async (values: InviteFormValues) => {
    if (!isPro && companyId) {
      const check = canInviteTeamMember('free', activeMemberCount);
      setUpgradeMessage(check.message || "Team management is only available on the Starter and Enterprise plans");
      setShowUpgradePrompt(true);
      return;
    }

    // Check plan limits
    const planCheck = canInviteTeamMember(company?.plan || 'free', activeMemberCount);
    if (!planCheck.allowed) {
      setUpgradeMessage(planCheck.message || "");
      setShowUpgradePrompt(true);
      return;
    }

    // Check if we can add a team member
    const canAdd = canAddTeamMember(
      activeMemberCount + 1, 
      company?.plan || 'free',
      company?.additional_members_stripe_subscription_id
    );

    if (!canAdd && company?.plan === 'free') {
      toast.error(`Free plan is limited to ${baseLimit} team member. Upgrade to add more members.`);
      return;
    }

    // Check if adding this member would require additional billing
    if (additionalMembersNeeded > 0 && company?.plan !== 'free') {
      const currentAdditional = company?.additional_team_members || 0;
      if (activeMemberCount + 1 > baseLimit + currentAdditional) {
        toast.error(`You need to purchase additional team member slots. Current limit: ${baseLimit + currentAdditional} members.`);
        return;
      }
    }

    setIsLoading(true);
    try {
      const { data: teamMember, error: teamMemberError } = await supabase
        .from("team_members")
        .insert({
          company_id: companyId,
          email: values.email,
          invited_by: (await supabase.auth.getUser()).data.user?.id,
        })
        .select()
        .single();

      if (teamMemberError) {
        if (teamMemberError.code === "23505") {
          toast.error("This email has already been invited");
        } else {
          console.error(teamMemberError);
          toast.error("Failed to create team member");
        }
        return;
      }

      const { error: inviteError } = await supabase.functions.invoke(
        "send-team-invite",
        {
          body: { teamMemberId: teamMember.id },
        }
      );

      if (inviteError) {
        console.error(inviteError);
        toast.error("Failed to send invitation email");
        return;
      }

      toast.success("Team member invited successfully");
      fetchTeamMembers();
    } catch (error) {
      console.error(error);
      toast.error("Something went wrong");
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdate = () => {
    fetchCompanyData();
    fetchTeamMembers();
  };

  return (
    <div className="space-y-6">
      {!isPro && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <p className="text-yellow-800">
            Team management is only available on the Starter and Enterprise plans. Upgrade to invite team members.
          </p>
        </div>
      )}

      {company && isPro && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium text-blue-900">
                Team Members: {activeMemberCount} / {baseLimit + (company.additional_team_members || 0)}
              </p>
              <p className="text-sm text-blue-700">
                Your {company.plan} plan includes {baseLimit} team members
                {company.additional_team_members > 0 && ` + ${company.additional_team_members} additional`}
              </p>
            </div>
          </div>
        </div>
      )}

      {company && isPro && (company.plan === 'starter' || company.plan === 'enterprise') && (
        <AdditionalMembersBilling
          company={company}
          currentMemberCount={activeMemberCount}
          onUpdate={handleUpdate}
        />
      )}

      <InviteMemberForm 
        onSubmit={onSubmit}
        isLoading={isLoading}
        isPro={isPro}
      />

      {isInitialLoading ? (
        <div className="text-center text-muted-foreground">Loading team members...</div>
      ) : teamMembers.length === 0 ? (
        <div className="text-center text-muted-foreground">
          No team members yet. Invite your first team member above!
        </div>
      ) : (
        <TeamMemberList 
          members={teamMembers} 
          onMemberRemoved={fetchTeamMembers}
        />
      )}

      <UpgradePrompt
        open={showUpgradePrompt}
        onOpenChange={setShowUpgradePrompt}
        message={upgradeMessage}
      />
    </div>
  );
};
